import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MessageSquare, Send, Trash2, AlertCircle, Mail } from "lucide-react";
// iter87.180.15 — BrutalConfirm for the async delete path so iOS
// Safari doesn't silently drop the confirm popup.
import { useBrutalConfirm } from "@/components/BrutalConfirm";

const MAX_LEN = 280;

/**
 * League Messages & Updates — combined message composer + activity feed for
 * the AdminPanel Settings tab. Admins see the full feed including audit rows
 * (rename / delete / code regenerate / etc.) and can post / retract their own
 * announcements. Plain text only, 280 chars.
 *
 * iter31l — optional email broadcast:
 *   - Checkbox "Also email this update" (default OFF)
 *   - Recipient scope: "players only" (default) | "players + pending invites"
 *   - Backend throttles to 1 broadcast / 5 min / league; UI shows countdown.
 */
export default function LeagueMessagesCard({ leagueId, isLocked }) {
    const [changes, setChanges] = useState([]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    // Email-blast controls
    const [notifyEmail, setNotifyEmail] = useState(false);
    const [recipientScope, setRecipientScope] = useState("players");
    const [counts, setCounts] = useState(null);
    // Live countdown for throttle lockout.
    const [secondsLeft, setSecondsLeft] = useState(0);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.listLeagueChanges(leagueId, 20);
            setChanges(r || []);
        } catch (e) {
            // Silent — surface only on user-initiated actions.
        }
        setLoading(false);
    }, [leagueId]);

    const loadCounts = useCallback(async () => {
        try {
            const r = await api.getMessageRecipientCounts(leagueId);
            setCounts(r);
            setSecondsLeft(r?.seconds_until_unlock || 0);
        } catch (e) {
            // Non-fatal — UI just hides counts.
        }
    }, [leagueId]);

    useEffect(() => { load(); loadCounts(); }, [load, loadCounts]);

    // Throttle countdown ticker.
    useEffect(() => {
        if (secondsLeft <= 0) return;
        const t = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, [secondsLeft]);

    const remaining = MAX_LEN - text.length;
    const tooLong = remaining < 0;
    const playersCount = counts?.players_with_email ?? 0;
    const invitesCount = counts?.pending_invites ?? 0;
    const recipientsTotal = recipientScope === "players_and_invites"
        ? playersCount + invitesCount
        : playersCount;
    const throttled = notifyEmail && secondsLeft > 0;
    const noRecipients = notifyEmail && recipientsTotal === 0;

    const submit = async () => {
        const trimmed = text.trim();
        if (!trimmed) { toast.error("Message can't be empty"); return; }
        if (trimmed.length > MAX_LEN) { toast.error(`Max ${MAX_LEN} characters`); return; }
        if (throttled) {
            toast.error(`Email blast throttled — wait ${secondsLeft}s.`);
            return;
        }
        if (noRecipients) {
            toast.error("No email recipients in the selected scope.");
            return;
        }
        setBusy(true);
        try {
            const res = await api.postLeagueMessage(leagueId, trimmed, {
                notifyEmail, recipientScope,
            });
            if (res.broadcast) {
                const { sent, failed } = res.broadcast;
                if (failed > 0) {
                    toast.warning(`Posted. ${sent} email${sent === 1 ? "" : "s"} sent, ${failed} failed.`);
                } else {
                    toast.success(`Posted & emailed to ${sent} recipient${sent === 1 ? "" : "s"}.`);
                }
            } else {
                toast.success("Message posted to your rivalry");
            }
            setText("");
            setNotifyEmail(false);
            await load();
            await loadCounts();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Post failed");
            // If the throttle bit us mid-flight, reload counts to refresh the timer.
            if (e.response?.status === 429) loadCounts();
        }
        setBusy(false);
    };

    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();

    const remove = async (row) => {
        const ok = await brutalConfirm({
            title: "Delete this message?",
            body: `"${row.summary}"`,
            confirmLabel: "Delete",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.deleteLeagueMessage(leagueId, row.id);
            toast.success("Message deleted");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div className="brutal-card p-5 space-y-4" data-testid="league-messages-card">
            <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-cyber" />
                <h2 className="font-display font-extrabold text-xl uppercase">Rivalry Messages & Updates</h2>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
                Post a short announcement that everyone in the rivalry will see on their dashboard. Admin-only — players see your messages plus draft milestones (start, complete, cleared).
            </p>

            <div className="space-y-2">
                <label className="block text-[10px] uppercase font-bold tracking-wider text-zinc-500">
                    Post a message <span className="text-zinc-600 normal-case font-medium tracking-normal">— plain text, 280 chars</span>
                </label>
                <textarea
                    data-testid="league-message-textarea"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        // Cmd/Ctrl+Enter to submit
                        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
                    }}
                    placeholder="e.g. Draft tonight at 7pm — make sure you've claimed your personal code."
                    rows={3}
                    maxLength={MAX_LEN + 50}
                    disabled={isLocked}
                    className="brutal-input w-full text-sm resize-y disabled:opacity-40"
                />

                {isLocked && (
                    <p className="text-[11px] text-zinc-500 italic" data-testid="league-messages-locked-notice">
                        This rivalry is closed or archived — messages are read-only.
                    </p>
                )}

                {/* Email broadcast controls */}
                <div className="brutal-card-light p-3 space-y-2" data-testid="league-message-email-block">
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input
                            data-testid="league-message-notify-email-checkbox"
                            type="checkbox"
                            checked={notifyEmail}
                            onChange={(e) => setNotifyEmail(e.target.checked)}
                            disabled={isLocked}
                            className="mt-0.5 accent-cyber disabled:opacity-40"
                        />
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 text-zinc-200">
                                <Mail className="w-3.5 h-3.5" /> Also email this update
                            </p>
                            <p className="text-[10px] text-zinc-500 leading-relaxed mt-0.5">
                                Sends your message as an email to recipients below. Throttled to 1 blast per 5 minutes per league.
                            </p>
                        </div>
                    </label>

                    {notifyEmail && (
                        <div className="pl-6 space-y-2" data-testid="league-message-recipient-scope">
                            <p className="text-[10px] uppercase font-bold tracking-wider text-zinc-500">Recipients</p>
                            <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                                <input
                                    data-testid="recipient-scope-players"
                                    type="radio"
                                    name="recipient-scope"
                                    value="players"
                                    checked={recipientScope === "players"}
                                    onChange={() => setRecipientScope("players")}
                                    className="accent-cyber"
                                />
                                <span>Crewmates only <span className="text-zinc-500">({playersCount})</span></span>
                            </label>
                            <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                                <input
                                    data-testid="recipient-scope-players-and-invites"
                                    type="radio"
                                    name="recipient-scope"
                                    value="players_and_invites"
                                    checked={recipientScope === "players_and_invites"}
                                    onChange={() => setRecipientScope("players_and_invites")}
                                    className="accent-cyber"
                                />
                                <span>Crewmates + pending invites <span className="text-zinc-500">({playersCount + invitesCount})</span></span>
                            </label>

                            {noRecipients && (
                                <p
                                    data-testid="recipient-empty-warning"
                                    className="text-[10px] text-heat flex items-center gap-1.5"
                                >
                                    <AlertCircle className="w-3 h-3" /> No recipients in this scope. Add crewmate emails first.
                                </p>
                            )}
                            {throttled && (
                                <p
                                    data-testid="broadcast-throttle-warning"
                                    className="text-[10px] text-heat flex items-center gap-1.5"
                                >
                                    <AlertCircle className="w-3 h-3" /> Blast throttled — wait <span className="font-mono font-bold">{secondsLeft}s</span> (1 / 5 min limit).
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between flex-wrap gap-2">
                    <p
                        data-testid="league-message-counter"
                        className={`text-[11px] ${tooLong ? "text-heat font-bold" : "text-zinc-500"}`}
                    >
                        {remaining} character{Math.abs(remaining) === 1 ? "" : "s"} left
                    </p>
                    <button
                        data-testid="league-message-submit-btn"
                        onClick={submit}
                        disabled={busy || !text.trim() || tooLong || throttled || noRecipients || isLocked}
                        className="brutal-btn-primary text-sm py-2 disabled:opacity-30"
                    >
                        <Send className="w-4 h-4" />
                        {notifyEmail
                            ? ` Post & email ${recipientsTotal}`
                            : " Post message"}
                    </button>
                </div>
            </div>

            {/* Feed */}
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="league-messages-feed">
                {!loading && changes.length === 0 && (
                    <p className="text-xs text-zinc-500 italic">No messages yet — your post will be the first.</p>
                )}
                {changes.map((c) => {
                    const isMsg = c.change_type === "admin_message";
                    return (
                        <div
                            key={c.id}
                            data-testid={`admin-change-row-${c.id}`}
                            className={`brutal-card-light p-3 text-xs flex items-start gap-2 ${isMsg ? "border-cyber/40" : ""}`}
                        >
                            <div className="flex-1 min-w-0">
                                {isMsg && (
                                    <p className="text-[9px] uppercase font-bold tracking-widest text-cyber mb-1 flex items-center gap-1.5">
                                        📣 Admin message
                                        {(c.actor_username || "").includes("(Co-Admin)") && (
                                            <span
                                                data-testid={`co-admin-badge-${c.id}`}
                                                className="normal-case tracking-normal text-[8px] font-bold px-1.5 py-0.5 rounded-sm bg-zinc-800 text-zinc-400 border border-zinc-700"
                                            >
                                                via Co-Admin
                                            </span>
                                        )}
                                    </p>
                                )}
                                <p className="text-zinc-300 leading-relaxed break-words">{c.summary}</p>
                                <p className="text-[10px] text-zinc-500 mt-1">
                                    {new Date(c.created_at).toLocaleString()}
                                </p>
                            </div>
                            {isMsg && (
                                <button
                                    data-testid={`admin-message-delete-${c.id}-btn`}
                                    onClick={() => remove(c)}
                                    disabled={isLocked}
                                    className="brutal-btn-ghost p-1.5 disabled:opacity-30"
                                    title="Delete this message"
                                >
                                    <Trash2 className="w-3.5 h-3.5 text-heat" />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {tooLong && (
                <p className="text-xs text-heat flex items-center gap-1.5" data-testid="league-message-too-long-warning">
                    <AlertCircle className="w-3 h-3" /> Trim {Math.abs(remaining)} character{Math.abs(remaining) === 1 ? "" : "s"} before posting.
                </p>
            )}
            {confirmHost}
        </div>
    );
}
